import { Component, EventEmitter, inject, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core'
import { Venue } from '../domain/Venue'
import { AbstractControl, FormGroup } from '@angular/forms'
import { Voucher } from '../domain/Voucher'
import { PricedSKU } from '../domain/SKU'
import { startWith, Subject, takeUntil } from 'rxjs'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { RichTextModalComponent } from '../rich-text-modal/rich-text-modal.component'
import { environment } from '../../environments/environment'
import type Quill from 'quill'

type VoucherSKUOptionViewModel = {
    sku: PricedSKU,
    amount: number,
    price: number,
    priceDiffersFromAmount: boolean,
}

@Component({
    selector: 'app-voucher-selection',
    templateUrl: './voucher-selection.component.html',
    styleUrls: ['./voucher-selection.component.sass'],
})
export class VoucherSelectionComponent implements OnInit, OnChanges, OnDestroy {

    @Input() venue!: Venue
    @Input() voucher!: Voucher
    @Input() form!: FormGroup
    @Output() checkoutSelected = new EventEmitter<void>()
    @ViewChild('checkoutFooter', { static: true }) checkoutFooter!: TemplateRef<unknown>
    selectedSKU: PricedSKU | null = null
    expiryDate: Date | null = null
    descriptionShowMore = false
    voucherDescriptionCanTruncate = false
    skuIdControl: AbstractControl | null = null
    quantityControl: AbstractControl | null = null
    skuOptions: VoucherSKUOptionViewModel[] = []
    lowestPricedSKUPrice: number | undefined
    quantityOptions = Array.from({ length: 20 }, (_, index) => index + 1)
    readonly environment = environment
    private modalService = inject(NgbModal)
    private ngZone = inject(NgZone)
    private descriptionResizeObserver: ResizeObserver | null = null
    private onDestroy$ = new Subject<void>()

    get postalDeliveryFee(): number | undefined {
        return this.voucher.postalDeliveryFee ?? undefined
    }

    get canPurchaseVoucher(): boolean {
        return Boolean(this.venue?.paymentsAccountForCapability('VoucherPayments'))
    }

    get totalPrice(): number | null {
        const quantity: unknown = this.quantityControl?.value
        if (!this.selectedSKU || typeof quantity !== 'number' || !Number.isInteger(quantity) || quantity < 1 || quantity > 20 || this.quantityControl?.invalid) {
            return null
        }
        return this.selectedSKU.price * quantity
    }

    ngOnInit() {
        this.bindFormToVoucherSelectedSKU()
    }

    ngOnChanges() {
        this.expiryDate = this.voucher.getExpiryDateFromDate(new Date())
        this.lowestPricedSKUPrice = this.voucher.lowestPricedSKU?.price
        this.skuOptions = this.voucher.skusSortedByPriceLowestFirst.map(sku => this.makeSKUOptionViewModel(sku))
        this.updateSelectedSKU(this.skuIdControl?.value ?? null)
        this.descriptionShowMore = false
    }

    ngOnDestroy() {
        this.descriptionResizeObserver?.disconnect()
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    showPolicy() {
        const modal = this.modalService.open(RichTextModalComponent)
        modal.componentInstance.title = 'Terms and Conditions'
        modal.componentInstance.content = this.voucher.policy
    }

    expandDescription() {
        this.descriptionShowMore = true
    }

    toggleDescription() {
        this.descriptionShowMore = !this.descriptionShowMore
    }

    goToCheckout() {
        if (!this.voucher.isOnSale || !this.canPurchaseVoucher || !this.selectedSKU) {
            return
        }
        this.form.markAllAsTouched()
        this.form.updateValueAndValidity()
        if (this.form.invalid || this.totalPrice === null) {
            return
        }
        this.checkoutSelected.emit()
    }

    private bindFormToVoucherSelectedSKU() {
        this.skuIdControl = this.form.get('skuId')
        this.quantityControl = this.form.get('quantity')
        if (!this.skuIdControl) {
            return
        }

        if (this.skuIdControl.value == null) {
            const firstSKU = this.voucher.skusSortedByPriceLowestFirst[0]
            if (firstSKU) {
                this.skuIdControl.setValue(firstSKU.id)
            }
        }

        this.skuIdControl.valueChanges
            .pipe(
                startWith(this.skuIdControl.value),
                takeUntil(this.onDestroy$)
            )
            .subscribe((skuId: string | null) => {
                this.updateSelectedSKU(skuId)
            })
    }

    observeDescription(editor: Quill) {
        this.descriptionResizeObserver?.disconnect()
        this.descriptionResizeObserver = new ResizeObserver(() => {
            const collapsedHeight = parseFloat(getComputedStyle(editor.root).lineHeight) * 5
            const canTruncate = editor.root.scrollHeight > collapsedHeight + 1
            if (canTruncate !== this.voucherDescriptionCanTruncate) {
                this.ngZone.run(() => {
                    this.voucherDescriptionCanTruncate = canTruncate
                })
            }
        })
        this.descriptionResizeObserver.observe(editor.root)
    }

    private makeSKUOptionViewModel(sku: PricedSKU): VoucherSKUOptionViewModel {
        const value = this.voucher.getValueForSKU(sku.id)
        const amount = value ? value.amount : sku.price
        return {
            sku,
            amount,
            price: sku.price,
            priceDiffersFromAmount: sku.price !== amount,
        }
    }

    private updateSelectedSKU(skuId: string | null) {
        this.selectedSKU = skuId ? this.voucher.getSKUWithId(skuId) : null
    }
}
